import { faRotateLeft } from '@fortawesome/pro-light-svg-icons'
import { CodeCustomerToShowTyInversed } from '@teinor/erp/types/company/config/parameter'
import { CustomerContactPersonModelType } from '@teinor/erp/types/company/customer/customerContactPerson'
import { useMemo } from 'react'
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import TwinTable from '../../../../../../baseComponents/TwinTable'
import BaseScreenTable from '../../../../../../baseComponents/TwinTable/BaseScreenTable'
import { TableComponentProps } from '../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderBoolean, CustomRenderString, CustomRenderStringTwoLinesTooltip, DeleteRow, EditRow } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import ModalChangeActiveCustomerContactPerson from '../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerContactsPersons/ModalChangeActiveCustomerContactPerson'
import ModalCreateCustomerContactPersonConnect from '../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerContactsPersons/ModalCreateCustomerContactPerson'
import ModalEditCustomerContactPerson from '../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerContactsPersons/ModalEditCustomerContactPerson'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { getConfigParam } from '../../../../../../utils/reducers/getters'

interface CustomerContactsProps extends ComponentWithPermissions { }

const CustomerContacts: React.FC<CustomerContactsProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()

    const pageData = {
        title: t('people', 'Personas'),
        newText: t('newPerson', 'Nueva persona'),
    }

    return (
        <BaseScreenTable TableComponent={TableCustomerContacts} CreateModalComponent={ModalCreateCustomerContactPersonConnect} EditModalComponent={ModalEditCustomerContactPerson} DeleteModalComponent={ModalChangeActiveCustomerContactPerson} pageData={pageData} userPermissions={userPermissions} extraModalData={{showLink: true}} />
    )
}

type TableCustomerContactsProps = & TableComponentProps & { }

const TableCustomerContacts: React.FC<TableCustomerContactsProps> = ({ setTableInstance, setOpened, userPermissions }) => {
    const { t } = useTwinTranslation()
    const fieldMemo = useMemo(() => {
        const codeCustomerToShow = getConfigParam('codeCustomerToShow')
        let fieldId: 'id' | 'code' = 'id'
        if (parseInt(String(codeCustomerToShow)) === parseInt(String(CodeCustomerToShowTyInversed['codeCustomer']))) {
            fieldId = 'code'
        }
        return fieldId
    }, [])
    const columns: ColumnTableSchema<CustomerContactPersonModelType> = [
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            searchKey: 'CustomerContactPerson.name',
            sortable: true,
            minWidth: 130,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            }
        },
        {
            id: 'CustomerId',
            dataKey: 'CustomerId',
            label: t('customer', 'Cliente'),
            searchKey: 'Customer.name',
            sortable: true,
            minWidth: 110,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={(allRowData.Customer?.[fieldMemo] ? (allRowData.Customer[fieldMemo] + ' - ') : '')  + (allRowData.Customer?.name || '')} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customer', 'Cliente') }
            }
        },
        {
            hideColumnByDefault: true,
            justForFilter: true,
            id: 'code',
            dataKey: 'CustomerId',
            label: '',
            searchKey: 'Customer.code',
            searchableComponent: {
                component: 'SearchableComponentBasic',
            },
        },
        {
            id: 'position',
            dataKey: 'position',
            label: t('position', 'Cargo'),
            sortable: true,
        },
        {
            id: 'mobile',
            dataKey: 'mobile',
            label: t('mobile', 'Móvil'),
            searchKey: 'CustomerContactPerson.mobile',
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('mobile', 'Móvil') }
            }
        },
        {
            id: 'phone',
            dataKey: 'phone',
            label: t('phone', 'Teléfono'),
            searchKey: 'CustomerContactPerson.phone',
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('phone', 'Teléfono') }
            }
        },
        {
            id: 'email',
            dataKey: 'email',
            label: t('email', 'Email'),
            searchKey: 'CustomerContactPerson.email',
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('email', 'Email') }
            }
        },
        {
            id: 'notes',
            dataKey: 'notes',
            label: t('notes', 'Notas'),
            hideColumnByDefault: true,
            customRender: (parameterValue, allRowData) => <CustomRenderStringTwoLinesTooltip id={allRowData?.rowIndex + 'notes'} value={parameterValue} />,
        },
        {
            id: 'active',
            dataKey: 'active',
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: {
                    items: {
                        'true': { name: t('actived', 'Activados') },
                        'false': { name: t('disableds', 'Desactivados') },
                        'all': { name: t('all', 'Todos') }
                    }, label: t('active', 'Activo')
                }
            },
            generalSearchable: false
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} icon={!allRowData?.active ? faRotateLeft : undefined} messageTooltip={!allRowData?.active ? t('reactivate', 'Reactivar') : t('deactivate', 'Desactivar')} />,
            havePermission: permissionCheck(userPermissions, 'delete')
        }
    ]

    return (
        <TwinTable columns={columns} name='allContactPersons' getDataFrom='/api/app/customer/customerContactPerson/getAllContactPersons'
            onRowClick={(_id, rowData) => setOpened({
                type: 'edit',
                allRowData: rowData
            })} setTableInstance={setTableInstance} />
    )
}

export default CustomerContacts